import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import { AssetUrls } from 'config/assets';
import { showError, showSuccess } from 'utils/toastNotifications';
import { useVerifyEmailQuery } from 'store/services/auth/authApi';
import { useGetMeQuery } from 'store/services/user/meApi';
import { trackEvent, getUserProperties } from 'utils/analytics';
import './AccountVerificationPage.css';

const AccountVerificationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);
  const hasTrackedEvent = useRef(false);

  const { data: verificationData, isLoading, error } = useVerifyEmailQuery(token || '', {
    skip: !token,
  });

  // Fetch user data for analytics (skip if not authenticated or verification failed)
  const { data: userData } = useGetMeQuery(undefined, {
    skip: !verificationData?.message,
  });

  // Track email verification in GA4 (only once)
  useEffect(() => {
    if (verificationData?.message && userData && !hasTrackedEvent.current) {
      hasTrackedEvent.current = true;
      trackEvent('email_verified', getUserProperties({
        id: userData.id,
        createdAt: userData.createdAt,
        country: userData.country,
      }));
    }
  }, [verificationData, userData]);

  // Handle successful verification
  useEffect(() => {
    if (verificationData?.message) {
      showSuccess(verificationData.message);

      // Start countdown for redirect
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate('/home');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [verificationData, navigate]);

  // Handle error cases
  useEffect(() => {
    if (error) {
      const errorMessage = (error as any)?.data?.message || 'This verification link is invalid or has expired.';
      showError(errorMessage);

      // Redirect to login after showing error
      setTimeout(() => navigate('/welcome'), 5000);
    }
  }, [error, navigate]);

  if (isLoading) {
    return (
      <div className="account-verification-page">
        <div className="account-verification-container">
          <div className="account-verification-loading">
            <div className="account-verification-spinner"></div>
            <p>Verifying your email...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="account-verification-page">
        <div className="account-verification-container">
          <div className="account-verification-logo">
            <img src={AssetUrls.logos.whiteSvg} alt="TradeMagic" />
          </div>

          <div className="account-verification-error">
            <div className="error-icon">✕</div>
            <h1 className="account-verification-title">Verification Failed</h1>
            <p className="error-message">
              This verification link is invalid or has expired.
            </p>
            <p className="error-submessage">
              Please log in and request a new verification email from your settings page.
            </p>
            <a href="/welcome" className="account-verification-link">
              Back to Login
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (verificationData) {
    return (
      <div className="account-verification-page">
        <div className="account-verification-container">
          <div className="account-verification-logo">
            <img src={AssetUrls.logos.whiteSvg} alt="TradeMagic" />
          </div>

          <div className="account-verification-success">
            <div className="success-icon">✓</div>
            <h1 className="account-verification-title">Email Verified!</h1>

            <p className="success-message">
              Your email address has been successfully verified.
            </p>

            {verificationData.email && (
              <p className="verified-email">
                <strong>{verificationData.email}</strong>
              </p>
            )}

            <p className="redirect-message">
              Redirecting to your dashboard in {countdown} seconds...
            </p>

            <p className="info-message">
              You can now send and receive cards on TradeMagic.
            </p>

            <a href="/home" className="account-verification-link">
              Go to Dashboard Now
            </a>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AccountVerificationPage;